import { create } from 'zustand';
import type { Conversation, Sentiment, Question, Draft, ChatMessage } from '@/lib/types';

interface ConversationState {
  currentConversation: Conversation | null;
  crewflowConversationId: string | null; // Crewflow UUID for chat endpoint
  sentiment: Sentiment | null;
  questions: Question[];
  answers: Record<string, string>;
  draft: Draft | null;
  chatHistory: ChatMessage[];
  isAnalyzing: boolean;
  isGenerating: boolean;

  // Feedback tracking
  originalDraftText: string | null;
  draftGeneratedAt: Date | null;
  feedbackSubmitted: boolean;

  setConversation: (conversation: Conversation) => void;
  setCrewflowConversationId: (id: string) => void;
  setSentiment: (sentiment: Sentiment) => void;
  setQuestions: (questions: Question[]) => void;
  answerQuestion: (questionId: string, answer: string) => void;
  setDraft: (draft: Draft) => void;
  addChatMessage: (message: ChatMessage) => void;
  setChatHistory: (messages: ChatMessage[]) => void;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  setIsGenerating: (isGenerating: boolean) => void;
  setFeedbackSubmitted: (submitted: boolean) => void;
  reset: () => void;
}

export const useConversationStore = create<ConversationState>((set) => ({
  currentConversation: null,
  crewflowConversationId: null,
  sentiment: null,
  questions: [],
  answers: {},
  draft: null,
  chatHistory: [],
  isAnalyzing: false,
  isGenerating: false,
  originalDraftText: null,
  draftGeneratedAt: null,
  feedbackSubmitted: false,

  setConversation: (conversation) => {
    // Track call count to detect loops
    if (!window.__setConversationCount) window.__setConversationCount = 0;
    window.__setConversationCount++;

    console.log(`[ConversationStore] setConversation called (count: ${window.__setConversationCount})`);
    console.log('[ConversationStore] Conversation ID:', conversation?.id);

    if (window.__setConversationCount > 5) {
      console.error('[ConversationStore] SET_CONVERSATION LOOP DETECTED! Called more than 5 times!');
      console.trace('Call stack:');
    }

    // Check if this is actually a new conversation (prevent unnecessary re-renders)
    set((state) => {
      if (state.currentConversation?.id === conversation?.id) {
        console.log('[ConversationStore] Same conversation ID, checking for changes...');
        // Check if messages changed
        const sameMessageCount = state.currentConversation?.messages?.length === conversation?.messages?.length;
        if (sameMessageCount) {
          console.log('[ConversationStore] Same conversation, skipping update to prevent re-render');
          return state; // No change, don't trigger re-render
        }
      }
      console.log('[ConversationStore] New or changed conversation, updating state');
      return { currentConversation: conversation };
    });
  },

  setCrewflowConversationId: (id) => set({ crewflowConversationId: id }),

  setSentiment: (sentiment) => set({ sentiment }),

  setQuestions: (questions) => set({ questions }),

  answerQuestion: (questionId, answer) =>
    set((state) => ({
      answers: {
        ...state.answers,
        [questionId]: answer,
      },
    })),

  setDraft: (draft) => set({
    draft,
    originalDraftText: draft.text,
    draftGeneratedAt: new Date(),
    feedbackSubmitted: false,
  }),

  addChatMessage: (message) =>
    set((state) => ({
      chatHistory: [...state.chatHistory, message],
    })),

  setChatHistory: (messages) => set({ chatHistory: messages }),

  setIsAnalyzing: (isAnalyzing) => set({ isAnalyzing }),

  setIsGenerating: (isGenerating) => set({ isGenerating }),

  setFeedbackSubmitted: (submitted) => set({ feedbackSubmitted: submitted }),

  reset: () =>
    set({
      crewflowConversationId: null,
      sentiment: null,
      questions: [],
      answers: {},
      draft: null,
      chatHistory: [],
      isAnalyzing: false,
      isGenerating: false,
      originalDraftText: null,
      draftGeneratedAt: null,
      feedbackSubmitted: false,
    }),
}));
